import { useState, useEffect } from 'react'
import { User } from 'firebase/auth'

interface RoleManagementProps {
  user: User
}

interface AppUser {
  uid: string
  role: string
}

const RoleManagement = ({ user }: RoleManagementProps) => {
  const [users, setUsers] = useState<AppUser[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedApp, setSelectedApp] = useState('split')

  const apps = ['split', 'flan']

  useEffect(() => {
    fetchUsers()
  }, [selectedApp])

  const fetchUsers = async () => {
    try {
      const token = await user.getIdToken()
      const response = await fetch(`/api/roles/${selectedApp}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setUsers(data.users || [])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  const assignRole = async (uid: string, role: string) => {
    try {
      const token = await user.getIdToken()
      const response = await fetch(`/api/roles/${selectedApp}/${uid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role })
      })
      
      if (response.ok) {
        fetchUsers()
      }
    } catch (error) {
      console.error('Error assigning role:', error)
    }
  }

  const removeRole = async (uid: string) => {
    try {
      const token = await user.getIdToken()
      const response = await fetch(`/api/roles/${selectedApp}/${uid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        fetchUsers()
      }
    } catch (error) {
      console.error('Error removing role:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-7xl mx-auto">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Role Management
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Manage user roles for different applications.
          </p>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Application
            </label>
            <select
              value={selectedApp}
              onChange={(e) => setSelectedApp(e.target.value)}
              className="mt-1 block w-full max-w-xs pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              {apps.map(app => (
                <option key={app} value={app}>{app}</option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((appUser) => (
                  <tr key={appUser.uid}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-all">
                      {appUser.uid}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        appUser.role === 'admin' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {appUser.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <select
                          value={appUser.role}
                          onChange={(e) => assignRole(appUser.uid, e.target.value)}
                          className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button
                          onClick={() => removeRole(appUser.uid)}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No users found for this application.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RoleManagement 